import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { FaCheck } from "react-icons/fa";
import { ImCancelCircle } from "react-icons/im";
import { FiExternalLink } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import toast from "react-hot-toast";
import { RiProgress5Line } from "react-icons/ri";
import Loading from "./Loading";
// Import the confirmation popup component used for delete confirmation.
import ConfirmationPopup from "./ConfirmationPopup";
import {
  isTrainingInProgressStatus,
  isTrainingSucceededStatus,
} from "../../utils/utils";

const formatTagAliases = (tagAliases, separator = "; ") =>
  tagAliases
    .replace(/[[\]']/g, "")
    .split(", ")
    .map((tag) => `${tag}`)
    .join(separator);

const renderTrainingStatusCell = (row) => {
  if (isTrainingSucceededStatus(row.TRAINING_STATUS)) {
    return (
      <div className="text-green-500 font-medium flex items-center gap-1">
        <FaCheck />
        Completed
      </div>
    );
  }

  if (isTrainingInProgressStatus(row.TRAINING_STATUS)) {
    return (
      <div className="text-yellow-600 font-medium flex items-center gap-1">
        <RiProgress5Line />
        In Progress
      </div>
    );
  }

  return (
    <div className="text-red-600 flex font-medium items-center gap-1.5">
      <ImCancelCircle /> Failed
    </div>
  );
};

const getTagDescription = (tagName, dataTagName) => {
  if (!tagName || (Array.isArray(tagName) && tagName.length === 0)) {
    if (!dataTagName) {
      return "-";
    }

    return dataTagName
      .replace(/\s*\([^)]*\)/g, "")
      .replace(/[[\]']/g, " ")
      .trim();
  }

  if (Array.isArray(tagName)) {
    return tagName
      .map((tag) => {
        if (typeof tag !== "string") {
          return "";
        }

        return tag.replace(/\s*\([^)]*\)/g, "").trim();
      })
      .join(", ");
  }

  if (typeof tagName === "string") {
    return tagName
      .split(",")
      .map((tag) => tag.replace(/\s*\([^)]*\)/g, "").trim())
      .join(", ");
  }

  return "-";
};

const getAlarmLimitsContent = (data) => {
  const tags = data.DEP_TAG_ALIAS.replace(/[[\]']/g, "")
    .split(", ")
    .map((tag) => tag.trim());

  let highLimits = [];
  if (
    Array.isArray(data.HIGH_LIMIT_ARRAY) &&
    data.HIGH_LIMIT_ARRAY.length > 0
  ) {
    highLimits = data.HIGH_LIMIT_ARRAY;
  }

  let lowLimits = [];
  if (Array.isArray(data.LOW_LIMIT_ARRAY) && data.LOW_LIMIT_ARRAY.length > 0) {
    lowLimits = data.LOW_LIMIT_ARRAY;
  }

  return tags
    .map(
      (tag, idx) =>
        `${tag} - High Alarm : ${highLimits[idx]}, Low Alarm : ${lowLimits[idx]}`,
    )
    .join("\n");
};

const renderBusinessRules = (data) => {
  if (!data.BUSINESS_RULES) {
    return "-";
  }

  let rules = [];
  try {
    rules = JSON.parse(data.BUSINESS_RULES.replace(/'/g, '"'));
  } catch (error) {
    console.error("Failed to parse business rules:", error);
    return "-";
  }

  return (
    <table className="mt-2 bg-white border border-gray-300 rounded">
      <thead>
        <tr className="bg-gray-100 font-normal">
          <th className="px-3 py-2 border-b text-left">Alias</th>
          <th className="px-3 py-2 border-b text-left">Operator</th>
          <th className="px-3 py-2 border-b text-left">Value</th>
        </tr>
      </thead>
      <tbody>
        {rules.map((rule, idx) => (
          <tr key={idx} className="border-b">
            <td className="px-3 py-2">{rule.ALIAS}</td>
            <td className="px-3 py-2">{rule.OPERATOR}</td>
            <td className="px-3 py-2">{rule.VALUE}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const ProjectInfoSection = ({ projectInfo }) => {
  const shouldShowTitle = projectInfo.length > 0;

  return (
    <div>
      {shouldShowTitle && (
        <p className="mb-1 text-base ml-16 font-semibold">Project Info </p>
      )}
      <ul>
        {projectInfo.map((info, index) => (
          <li key={index}>
            <p className="mb-1 ">
              <strong>Make Manufacturer:</strong> {info.MAKE_MANUFACTURER}
              <br />
            </p>
            <p className="mb-1">
              <strong>Seal Type:</strong> {info.SEAL_TYPE}
              <br />
            </p>
            <p className="mb-1">
              <strong>Design Pressure (Bar):</strong>{" "}
              {info.DESIGN_PRESSURE_BAR || "N/A"}
              <br />
            </p>
            <p className="mb-1">
              <strong>Direction of Rotation:</strong>{" "}
              {info.DIRECTION_OF_ROTATION}
              <br />
            </p>
            <p className="mb-1">
              <strong>Trip Speed (RPM):</strong> {info.TRIP_SPEED_RPM || "N/A"}
              <br />
            </p>
            <p className="mb-1">
              <strong>Maximum Operating Speed (RPM):</strong>{" "}
              {info.MAXIMUM_OPERATING_SPEED_RPM}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ModelsDataTable = ({
  tagName,
  modelsData,
  count,
  updateModelStatus,
  loading,
  setLoading,
  deleteModel,
  reloadModels,
}) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [rows, setRows] = useState(modelsData);

  const [isPopupVisible, setIsPopupVisible] = useState(false);
  // State to track the model pending delete confirmation.
  const [modelToDelete, setModelToDelete] = useState(null);

  const handleToggle = async (index) => {
    let newRows = [];
    setRows((prevRows) => {
      newRows = prevRows.map((row, i) => {
        if (i === index) {
          let nextStatus = "ACTIVE";
          if (row.STATUS === "ACTIVE") {
            nextStatus = "INACTIVE";
          }

          return {
            ...row,
            STATUS: nextStatus,
          };
        }
        return row;
      });
      return newRows;
    });

    setLoading(true);

    try {
      await updateModelStatus(newRows[index].MODEL_ID, newRows[index].STATUS);
      toast.success("Active status updated successfully!");
      if (reloadModels) {
        reloadModels();
      }
    } catch (error) {
      toast.error("Error while updating active status. Please try again!");
      console.error("Error updating model active status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (index) => {
    setModelToDelete(index);
    setIsPopupVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (modelToDelete !== null) {
      setLoading(true);
      try {
        const res = await deleteModel(rows[modelToDelete].MODEL_ID);
        if (res === "Model successfully deleted") {
          toast.success("Model deleted successfully!");
          setRows((prevRows) => prevRows.filter((_, i) => i !== modelToDelete));
        } else {
          toast.error("Model cannot be deleted");
        }
      } catch (error) {
        toast.error("Error while deleting model. Please try again!");
        console.error("Error deleting model:", error);
      } finally {
        setLoading(false);
        setIsPopupVisible(false);
        setModelToDelete(null);
      }
    }
  };

  const handleCancel = () => {
    setIsPopupVisible(false);
    setModelToDelete(null);
  };

  const handleRowClick = (row) => {
    if (expandedRow === row) {
      setExpandedRow(null);
      return;
    }

    setExpandedRow(row);
  };

  const handleGotoInference = (row) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        datasource: row.DATASOURCE,
        dgsTags: formatTagAliases(row.DEP_TAG_ALIAS, ","),
        startDate: row.TRAINING_ST_DATE,
        endDate: row.TRAINING_END_DATE,
      });

      const url = `${window.location.origin}/inference?${params.toString()}`;

      setTimeout(() => {
        window.open(url, "_blank");
        setLoading(false);
      }, 10000);
    } catch (error) {
      console.error("Error in handleGotoInference:", error);
      setLoading(false);
    }
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row.CUSTOM_NAME,
      sortable: true,
      grow: 1.5,
      cell: (row) => (
        <div
          title={row.CUSTOM_NAME}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.CUSTOM_NAME}
        </div>
      ),
    },
    {
      name: "Assembly",
      selector: (row) => row.DRIVER,
      sortable: true,
      width: "130px",
      cell: (row) => (
        <div
          title={row.DRIVER}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.DRIVER}
        </div>
      ),
    },
    {
      name: "DGS Tags",
      selector: (row) => formatTagAliases(row.DEP_TAG_ALIAS),
      sortable: true,
      grow: 1,
      cell: (row) => {
        const tags = formatTagAliases(row.DEP_TAG_ALIAS);
        return (
          <div
            title={tags}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {tags}
          </div>
        );
      },
    },
    {
      name: "Create Date",
      selector: (row) => row.CREATED_AT,
      sortable: true,
      width: "117px",
      cell: (row) => (
        <div
          title={row.CREATED_AT}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100px",
          }}
        >
          {row.CREATED_AT}
        </div>
      ),
    },
    {
      name: "Start Date",
      selector: (row) => row.TRAINING_ST_DATE,
      sortable: true,
      width: "107px",
      cell: (row) => (
        <div
          title={row.TRAINING_ST_DATE}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "90px",
          }}
        >
          {row.TRAINING_ST_DATE}
        </div>
      ),
    },
    {
      name: "End Date",
      selector: (row) => row.TRAINING_END_DATE,
      sortable: true,
      width: "102px",
      cell: (row) => (
        <div
          title={row.TRAINING_END_DATE}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "90px",
          }}
        >
          {row.TRAINING_END_DATE}
        </div>
      ),
    },
    {
      name: "Training Status",
      cell: (row) => renderTrainingStatusCell(row),
      sortable: true,
      width: "135px",
    },
    {
      name: "Active",
      cell: (row, index) => (
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={row.STATUS === "ACTIVE"}
            onChange={() => handleToggle(index)}
          />
          <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-300 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
        </label>
      ),
      width: "69px",
    },
    {
      name: "Inference",
      cell: (row, index) => {
        let inferenceIconClass = "";
        let onInferenceClick = () => handleGotoInference(row);

        if (row.STATUS !== "ACTIVE") {
          inferenceIconClass = "opacity-50 cursor-not-allowed";
          onInferenceClick = () => {};
        }

        let deleteIconClass = "cursor-pointer text-red-600";
        let onDeleteClick = () => handleDeleteClick(index);

        if (row.STATUS === "ACTIVE") {
          deleteIconClass =
            "cursor-pointer text-red-600 opacity-50 cursor-not-allowed";
          onDeleteClick = () => {};
        }

        return (
          <div className="cursor-pointer text-btn-primary flex gap-4  ">
            <FiExternalLink
              className={inferenceIconClass}
              size={20}
              onClick={onInferenceClick}
            />
            <MdDelete
              onClick={onDeleteClick}
              className={deleteIconClass}
              size={20}
            />
          </div>
        );
      },
      width: "87px",
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        fontSize: "13px",
        backgroundColor: "#e3e1e1",
      },
    },
    cells: {
      style: {
        fontSize: "13.4px",
      },
    },
    table: {
      style: {
        border: "1px solid #e3e1e1",
        margin: "0 auto",
      },
    },
  };

  const conditionalRowStyles = [
    {
      when: (row) => row === expandedRow,
      style: {
        borderBottom: "none",
        backgroundColor: "#ededed",
      },
    },
  ];

  return (
    <>
      {loading && (
        <div className="flex flex-col items-center justify-center w-full h-32">
          <span className="mt-2 text-gray-700">Loading table...</span>
        </div>
      )}
      {!loading && (
        <div className="bg-white border border-gray-200 w-[65%] rounded-lg shadow-md flex flex-col">
          <DataTable
            columns={columns}
            data={rows}
            paginationTotalRows={count}
            pagination
            paginationPerPage={5}
            paginationRowsPerPageOptions={[5, 10, 15, 20]}
            highlightOnHover
            responsive
            customStyles={customStyles}
            expandableRows
            expandOnRowClicked
            expandableRowsHideExpander
            expandableRowExpanded={(row) => row === expandedRow}
            onRowClicked={handleRowClick}
            expandableRowsComponent={(props) => (
              <ExpandedComponent {...props} tagName={tagName} />
            )}
            conditionalRowStyles={conditionalRowStyles}
          />
        </div>
      )}
      {isPopupVisible && (
        <ConfirmationPopup
          message={"Are you sure you want to delete this model?"}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancel}
          cancelButtonMsg={"No, cancel"}
          confirmButtonMsg={"Yes, I'm sure"}
        />
      )}
    </>
  );
};

export default ModelsDataTable;

const ExpandedComponent = ({ data, tagName }) => {
  const alarmLimits = getAlarmLimitsContent(data);
  const businessRulesContent = renderBusinessRules(data);
  const tagDescription = getTagDescription(tagName, data.TAG_NAME);
  const dgsTags = formatTagAliases(data.DEP_TAG_ALIAS);
  let projectInfo = [];
  if (Array.isArray(data.PROJECT_INFO)) {
    projectInfo = data.PROJECT_INFO;
  }

  return (
    <div
      style={{
        padding: "10px 20px",
        fontSize: "13px",
        backgroundColor: "#f9f9f9",
        display: "flex",
        gap: "10px",
      }}
    >
      <div className="w-[450px]">
        <p className="mb-1">
          <strong>Custom Name : </strong> {data.CUSTOM_NAME}
        </p>
        <p className="mb-1">
          <strong>Custom Description : </strong> {data.CUSTOM_DESC ?? "-"}
        </p>
        <p className="mb-1 ">
          <strong>DGS Tags : </strong> {dgsTags}
        </p>
        <p className="mb-1">
          <strong>Tag Description : </strong> {tagDescription}
        </p>
        <p className="mb-1 ">
          <strong>Alarm Limits : </strong>
          <br />
          <pre style={{ margin: 0, padding: 0, background: "none" }}>
            {alarmLimits}
          </pre>
        </p>
        <p className="mb-1">
          <strong>Model Name : </strong> {data.MODEL_ID}
        </p>
        <p className="mb-1">
          <strong>Datasource Id : </strong> {data.DATASOURCE}
        </p>
        <p className="mb-1">
          <strong>Project Name : </strong> {data.PROJECTNAME}
        </p>
        <p className="mb-1">
          <strong>Driver : </strong>
          {data.DRIVER}
        </p>
        <p className="mb-1">
          <strong>Frequency : </strong>
          {data.DATA_FREQ ?? "-"} {data.DATA_FREQ && "mins"}
        </p>
        <p className="mb-1">
          <strong>Maximum Operating Speed:</strong> {data.MAX_OPR_SPEED ?? "-"}
        </p>
        <p className="mb-1">
          <strong>Minimum Operating Speed:</strong> {data.MIN_OPR_SPEED ?? "-"}
        </p>
        <div className="mb-1">
          <strong>Business Rule : </strong> {businessRulesContent}
        </div>
      </div>

      <ProjectInfoSection projectInfo={projectInfo} />
    </div>
  );
};
